import type { QueryColumn, QueryResultSet } from '../../../../types/queryExecution';
import { safeToISOString } from '../../../../utils/dateUtils';
import { defaultValuesInsert } from '../../../../utils/defaultValuesInsert';

type EngineId = 'mssql' | 'pg' | 'mysql' | 'sqlite' | 'oracle' | 'mock' | string;

/**
 * How generated INSERTs treat IDENTITY / auto-increment columns.
 * - `include-wrapped` (default): keep the identity values and, on MSSQL, wrap
 *   the statements in `SET IDENTITY_INSERT <table> ON/OFF` so the script runs
 *   instead of failing with Msg 544. Never silently changes IDs.
 * - `exclude`: drop identity columns from the column list and every VALUES
 *   tuple, letting the target assign fresh IDs.
 */
export type InsertIdentityMode = 'include-wrapped' | 'exclude';

type InsertBuilderOptions = {
  engineId?: EngineId;
  tableName?: string;
  batchSize?: number;
  /**
   * IDENTITY / auto-increment column names of the resolved source table
   * (matched against result-set column names case-insensitively). Omit or pass
   * an empty array to get the historical, identity-unaware output verbatim.
   */
  identityColumns?: readonly string[];
  /** Defaults to `include-wrapped` — see `InsertIdentityMode`. */
  identityMode?: InsertIdentityMode;
};

const UNKNOWN_TABLE = '/* unknown_table */';

function quoteIdentifier(name: string, engineId?: EngineId): string {
  if (!name) return name;
  const needsQuoting = /[^a-zA-Z0-9_]/.test(name) || /^\d/.test(name);
  if (!needsQuoting) return name;
  if (engineId === 'mssql') return `[${name.replace(/\]/g, ']]')}]`;
  if (engineId === 'mysql') return `\`${name.replace(/`/g, '``')}\``;
  return `"${name.replace(/"/g, '""')}"`;
}

function quoteTableName(schema: string | undefined, table: string, engineId?: EngineId): string {
  const quotedTable = quoteIdentifier(table, engineId);
  if (!schema) return quotedTable;
  return `${quoteIdentifier(schema, engineId)}.${quotedTable}`;
}

/**
 * The single source table every non-computed column comes from, or `null` for
 * joins / expressions / multi-table projections. Shared with the identity
 * metadata fetcher so "can we resolve a table?" has exactly one definition —
 * when this returns `null`, generation stays identity-unaware (today's output).
 */
export function resolveSingleSourceTable(
  columns: readonly { sourceSchema?: string; sourceTable?: string; isComputed?: boolean }[],
): { schema?: string; table: string } | null {
  const firstWithSource = columns.find((c) => c.sourceTable && !c.isComputed);
  if (!firstWithSource?.sourceTable) return null;

  const allSameTable = columns
    .filter((c) => c.sourceTable && !c.isComputed)
    .every((c) => c.sourceSchema === firstWithSource.sourceSchema && c.sourceTable === firstWithSource.sourceTable);

  if (!allSameTable) return null;

  return { schema: firstWithSource.sourceSchema, table: firstWithSource.sourceTable };
}

function resolveTableName(columns: QueryColumn[], options?: InsertBuilderOptions): string {
  if (options?.tableName) return options.tableName;

  const source = resolveSingleSourceTable(columns);
  if (!source) return UNKNOWN_TABLE;

  return quoteTableName(source.schema, source.table, options?.engineId);
}

function formatValue(value: unknown, engineId?: EngineId): string {
  if (value === null || value === undefined) return 'NULL';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'bigint') return String(value);
  if (typeof value === 'boolean') {
    if (engineId === 'mssql') return value ? '1' : '0';
    return value ? 'TRUE' : 'FALSE';
  }
  if (value instanceof Date) return `'${safeToISOString(value)}'`;
  if (typeof value === 'object') {
    const json = JSON.stringify(value);
    return `'${json.replace(/'/g, "''")}'`;
  }
  const str = String(value);
  return `'${str.replace(/'/g, "''")}'`;
}

function supportsMultiRowInsert(engineId?: EngineId): boolean {
  return engineId !== 'oracle';
}

function maxBatchSize(engineId?: EngineId): number {
  if (engineId === 'mssql') return 1000;
  return 1000;
}

/**
 * How the projection should be emitted once IDENTITY membership is known.
 * `all-defaults` is the degenerate case: `exclude` was asked for and NOTHING is
 * left to insert, so each row becomes an engine-specific all-defaults INSERT.
 */
type IdentityPlan =
  | { kind: 'columns'; columns: QueryColumn[]; wrapIdentityInsert: boolean }
  | { kind: 'all-defaults'; firstColumn: QueryColumn };

/**
 * Split the projection by IDENTITY membership and decide whether MSSQL needs
 * the `SET IDENTITY_INSERT` wrapper.
 *
 * Why only MSSQL wraps: `SET IDENTITY_INSERT` is a SQL Server session toggle
 * (mirrors `electron/data-compare/dataScriptGenerator.ts`). PG's
 * `OVERRIDING SYSTEM VALUE` lives inside the statement (different shape), and
 * cannot be emitted from what we know here — `is_identity` on PG is
 * `pg_get_serial_sequence(...) IS NOT NULL`, which is equally true for a plain
 * `serial` column, where `OVERRIDING SYSTEM VALUE` is rejected. MySQL / SQLite
 * / Oracle accept explicit values into an auto-increment column, so the Msg 544
 * bug class is MSSQL-specific in practice.
 *
 * D4 (codex pre-PR review) — `exclude` used to silently reverse itself into
 * include-wrapped when every projected column was IDENTITY (`SELECT id FROM t`
 * with "Skip identity columns" on). That made the preference do the opposite of
 * its label on every engine, and on PostgreSQL it emitted a bare explicit
 * identity value that `GENERATED ALWAYS AS IDENTITY` rejects. The request is
 * now honoured with an all-defaults row instead.
 */
function applyIdentityMode(
  columns: QueryColumn[],
  tableName: string,
  options: InsertBuilderOptions | undefined,
): IdentityPlan {
  const identityNames = options?.identityColumns ?? [];
  if (identityNames.length === 0) return { kind: 'columns', columns, wrapIdentityInsert: false };

  const identitySet = new Set(identityNames.map((n) => n.toLowerCase()));
  const remaining = columns.filter((c) => !identitySet.has(c.name.toLowerCase()));
  // Nothing identity-flavoured in this projection → nothing to do.
  if (remaining.length === columns.length) return { kind: 'columns', columns, wrapIdentityInsert: false };

  if ((options?.identityMode ?? 'include-wrapped') === 'exclude') {
    if (remaining.length > 0) return { kind: 'columns', columns: remaining, wrapIdentityInsert: false };
    return { kind: 'all-defaults', firstColumn: columns[0] };
  }

  return {
    kind: 'columns',
    columns,
    wrapIdentityInsert: options?.engineId === 'mssql' && tableName !== UNKNOWN_TABLE,
  };
}

export function resultSetToInsertStatements(
  resultSet: QueryResultSet,
  options?: InsertBuilderOptions
): string {
  const allColumns = resultSet.columns ?? [];
  const rows = resultSet.rows ?? [];
  if (allColumns.length === 0 || rows.length === 0) return '';

  const tableName = resolveTableName(allColumns, options);
  const plan = applyIdentityMode(allColumns, tableName, options);

  // D4 — "Skip identity columns" with an identity-only projection. Honour it
  // with one all-defaults row per source row (the target assigns the values)
  // rather than reversing the choice back to explicit identity values.
  if (plan.kind === 'all-defaults') {
    const statement = defaultValuesInsert({
      engineId: options?.engineId,
      tableName,
      quotedColumn: quoteIdentifier(plan.firstColumn.name, options?.engineId),
    });
    return [
      '-- Every selected column is IDENTITY; inserting default rows so the target assigns the values.',
      ...rows.map(() => statement),
    ].join('\n');
  }

  const { columns, wrapIdentityInsert } = plan;
  const columnNames = columns.map((c) => quoteIdentifier(c.name, options?.engineId)).join(', ');
  const engineId = options?.engineId;
  const batchSize = options?.batchSize ?? maxBatchSize(engineId);
  const useMultiRow = supportsMultiRowInsert(engineId) && rows.length > 1;

  const lines: string[] = [];

  if (wrapIdentityInsert) lines.push(`SET IDENTITY_INSERT ${tableName} ON;`);

  if (useMultiRow) {
    for (let i = 0; i < rows.length; i += batchSize) {
      const batch = rows.slice(i, i + batchSize);
      lines.push(`INSERT INTO ${tableName} (${columnNames}) VALUES`);
      for (let j = 0; j < batch.length; j++) {
        const row = batch[j];
        const values = columns.map((col) => formatValue(row[col.id ?? col.name], engineId)).join(', ');
        const suffix = j < batch.length - 1 ? ',' : ';';
        lines.push(`  (${values})${suffix}`);
      }
    }
  } else {
    for (const row of rows) {
      const values = columns.map((col) => formatValue(row[col.id ?? col.name], engineId)).join(', ');
      lines.push(`INSERT INTO ${tableName} (${columnNames}) VALUES (${values});`);
    }
  }

  if (wrapIdentityInsert) lines.push(`SET IDENTITY_INSERT ${tableName} OFF;`);

  return lines.join('\n');
}
